import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import InviteUserModal from './InviteUserModal';
import InviteLinkModal from './InviteLinkModal';
import UserDeleteModal from './UserDeleteModal';
import { useParadigm } from '../../contexts/ParadigmContext';

const AdminDashboard = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'users';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [showUserDeleteModal, setShowUserDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [showUrbanrideModal, setShowUrbanrideModal] = useState(false);
  const [urbanrideUser, setUrbanrideUser] = useState(null);
  const [rbacStatus, setRbacStatus] = useState(null);
  const [executiveProfiles, setExecutiveProfiles] = useState([]);
  const { user, actingOrganization } = useAuth();

  const isSuperadmin = user?.role === 'superadmin';
  const isOrgAdmin = user?.is_organization_admin;

  // Helper to check RBAC permission
  const hasRbacPermission = (permission) => {
    if (!rbacStatus?.rbac_enabled) return false;
    const userPerms = rbacStatus?.user_permissions || [];
    if (userPerms.includes('org_admin')) return true;
    return userPerms.includes(permission);
  };

  // Check if user has access via RBAC
  const hasRbacAccess = hasRbacPermission('users_invites');
  const hasAccess = isSuperadmin || isOrgAdmin || hasRbacAccess;

  // Fetch RBAC status on mount
  useEffect(() => {
    const fetchRbacStatus = async () => {
      try {
        const res = await axios.get(`${getApiBase()}/rbac/status`, { headers: getAuthHeaders() });
        setRbacStatus(res.data);
      } catch (err) {
        // RBAC may not be enabled
        console.log('RBAC status not available');
      }
    };
    fetchRbacStatus();
  }, []);

  useEffect(() => {
    if (hasAccess) {
      loadData();
    }
  }, [user, activeTab, hasAccess]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const requests = [
        axios.get(`${apiBase}/admin/users`, { headers }),
        axios.get(`${apiBase}/admin/invites`, { headers })
      ];

      // Only superadmins need the organizations list for the invite modal
      if (isSuperadmin) {
        requests.push(axios.get(`${apiBase}/admin/organizations`, { headers }));
      }

      const responses = await Promise.all(requests);
      const [usersResponse, invitesResponse, orgsResponse] = responses;

      setUsers(usersResponse.data);
      setInvites(invitesResponse.data);

      if (orgsResponse) {
        setOrganizations(orgsResponse.data);
      } else if (user?.organization_id) {
        // For org admins, create a single-item list with their org
        setOrganizations([{
          id: user.organization_id,
          name: user.organization_name || 'Your Organization'
        }]);
      }

      // Fetch executive profiles for linking executive users
      try {
        const execResponse = await axios.get(`${apiBase}/executives`, { headers });
        setExecutiveProfiles(Array.isArray(execResponse.data) ? execResponse.data : []);
      } catch (execErr) {
        console.log('Could not fetch executives:', execErr.message);
        setExecutiveProfiles([]);
      }
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (inviteData) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // For org admins, ensure the organization_id is their own
      const finalInviteData = isOrgAdmin && !isSuperadmin
        ? { ...inviteData, organization_id: user.organization_id }
        : inviteData;

      const response = await axios.post(`${apiBase}/admin/invite-user`, finalInviteData, { headers });
      setShowInviteUserModal(false);

      if (response.data.status === 'added_to_org') {
        // Existing user was added to the organization as a multi-org member
        setError('');
        const orgName = response.data.organization_name || 'the organization';
        alert(`${response.data.email} has been added to ${orgName}. They can now switch to this organization from their dashboard.`);
        loadData();
      } else if (response.data.status === 'already_member') {
        // User is already a member — show info message
        setError('');
        const orgName = response.data.organization_name || 'the organization';
        alert(`${response.data.email} is already a member of ${orgName}.`);
        loadData();
      } else if (response.data.status === 'approved') {
        // New user invite — show invite link modal
        setError('');

        // Find organization name
        const organization = organizations.find(org => org.id === finalInviteData.organization_id);
        const organizationName = organization ? organization.name : 'the organization';

        setInviteResult({
          inviteLink: response.data.invite_link,
          inviteToken: response.data.invite_token,
          email: inviteData.email,
          organizationName: organizationName,
          role: inviteData.role,
          emailSent: response.data.email_sent || false
        });

        setShowInviteLinkModal(true);
        loadData();
      }
    } catch (err) {
      console.error('Failed to invite user:', err);
      setError('Failed to invite user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleApproveInvite = async (inviteId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/approve-invite/${inviteId}`, {}, { headers });
      loadData();
    } catch (err) {
      setError('Failed to approve invite: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRejectInvite = async (inviteId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/reject-invite/${inviteId}`, {}, { headers });
      loadData();
    } catch (err) {
      setError('Failed to reject invite: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRemoveUser = async (userId) => {
    const userToRemove = users.find(u => u.id === userId);
    if (!userToRemove) {
      setError('User not found');
      return;
    }

    // Prevent org admins from removing superadmins
    if (!isSuperadmin && userToRemove.role === 'superadmin') {
      setError('You cannot remove a superadmin user');
      return;
    }

    setUserToDelete(userToRemove);
    setShowUserDeleteModal(true);
  };

  const confirmUserDelete = async () => {
    if (!userToDelete) return;

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/admin/users/${userToDelete.id}`, { headers });

      const updatedUsers = users.filter(u => u.id !== userToDelete.id);
      setUsers(updatedUsers);
      setError('');

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md shadow-lg z-50';
      successMessage.textContent = `User ${userToDelete.first_name} ${userToDelete.last_name} removed successfully`;
      document.body.appendChild(successMessage);

      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 3000);
    } catch (error) {
      console.error('Delete user error:', error);
      let errorMessage = 'Failed to remove user';

      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You cannot remove this user.';
      } else if (error.response?.status === 404) {
        errorMessage = 'User not found or already removed.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      setError(errorMessage);
    } finally {
      setShowUserDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const cancelUserDelete = () => {
    setShowUserDeleteModal(false);
    setUserToDelete(null);
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/users/${userId}/role`, { role: newRole }, { headers });
      loadData();
    } catch (err) {
      setError('Failed to update user role: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleLinkExecutive = async (userId, executiveId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/users/${userId}/link-executive`,
        { executive_id: executiveId || null },
        { headers }
      );
      loadData();
    } catch (err) {
      setError('Failed to link executive profile: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateOrgAdmin = async (userId, isOrgAdminStatus) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/users/${userId}/organization-admin`,
        { is_organization_admin: isOrgAdminStatus },
        { headers }
      );
      loadData();
    } catch (err) {
      setError('Failed to update organization admin status: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUrbanrideSettings = (userItem) => {
    setUrbanrideUser(userItem);
    setShowUrbanrideModal(true);
  };

  // Access check - superadmins, org admins, and RBAC users_invites can access
  if (!hasAccess) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-6xl mb-4">🚫</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You need admin privileges to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-4 sm:p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-blue-100 text-sm sm:text-base truncate">
              {isSuperadmin
                ? 'Manage users, invites, and Urbanride account linking'
                : 'Manage users and invites for your organization'}
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow p-1">
        <div className="grid grid-cols-3 gap-1">
          {[
            { id: 'users', label: 'Users', icon: '👥' },
            { id: 'invites', label: 'Invites', icon: '📧' },
            { id: 'settings', label: isSuperadmin ? 'Urbanride Settings' : 'Settings', icon: '🚗' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'users' && (
        <UsersTab
          users={users}
          organizations={organizations}
          isSuperadmin={isSuperadmin}
          currentUser={user}
          onInviteUser={() => setShowInviteUserModal(true)}
          onRemoveUser={handleRemoveUser}
          onUpdateUserRole={handleUpdateUserRole}
          onUpdateOrgAdmin={handleUpdateOrgAdmin}
          onUrbanrideSettings={handleUrbanrideSettings}
          executiveProfiles={executiveProfiles}
          onLinkExecutive={handleLinkExecutive}
        />
      )}

      {activeTab === 'invites' && (
        <InvitesTab
          invites={invites}
          onApproveInvite={handleApproveInvite}
          onRejectInvite={handleRejectInvite}
        />
      )}

      {activeTab === 'settings' && (
        <SettingsTab isSuperadmin={isSuperadmin} />
      )}

      {/* Modals */}
      {showInviteUserModal && (
        <InviteUserModal
          organizations={organizations}
          onClose={() => setShowInviteUserModal(false)}
          onInvite={handleInviteUser}
          isSuperadmin={isSuperadmin}
          currentUserOrgId={user?.organization_id}
        />
      )}

      {showInviteLinkModal && inviteResult && (
        <InviteLinkModal
          inviteLink={inviteResult.inviteLink}
          inviteToken={inviteResult.inviteToken}
          email={inviteResult.email}
          organizationName={inviteResult.organizationName}
          role={inviteResult.role}
          emailSentAutomatically={inviteResult.emailSent}
          onClose={() => {
            setShowInviteLinkModal(false);
            setInviteResult(null);
          }}
          onSendEmail={null}
        />
      )}

      {showUserDeleteModal && userToDelete && (
        <UserDeleteModal
          user={userToDelete}
          onConfirm={confirmUserDelete}
          onCancel={cancelUserDelete}
        />
      )}

      {showUrbanrideModal && urbanrideUser && (
        <UrbanrideSettingsModal
          user={urbanrideUser}
          onClose={() => {
            setShowUrbanrideModal(false);
            setUrbanrideUser(null);
          }}
        />
      )}
    </div>
  );
};

// Urbanride Settings Modal Component (Superadmin only)
const UrbanrideSettingsModal = ({ user, onClose }) => {
  const [settings, setSettings] = useState({
    test_api_key_configured: false,
    test_api_key_hint: null,
    production_api_key_configured: false,
    production_api_key_hint: null,
    mode: null  // null means inherit from org
  });
  const [testApiKey, setTestApiKey] = useState('');
  const [productionApiKey, setProductionApiKey] = useState('');
  const [modeOverride, setModeOverride] = useState('inherit');  // 'inherit', 'test', or 'production'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApiKeys, setShowApiKeys] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [user.id]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/admin/users/${user.id}/urbanride-settings`, { headers });
      setSettings(res.data);
      // Set mode override: if mode is set, use it; otherwise 'inherit'
      setModeOverride(res.data.mode || 'inherit');
    } catch (e) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      // Send mode: '' to clear override (inherit from org), or 'test'/'production' to set override
      const payload = { mode: modeOverride === 'inherit' ? '' : modeOverride };
      if (testApiKey) payload.test_api_key = testApiKey;
      if (productionApiKey) payload.production_api_key = productionApiKey;

      const res = await axios.put(`${apiBase}/admin/users/${user.id}/urbanride-settings`, payload, { headers });
      setSettings(res.data);
      setModeOverride(res.data.mode || 'inherit');
      setSuccess('Settings saved successfully');
      setTestApiKey('');
      setProductionApiKey('');
      setShowApiKeys(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleClearKey = async (keyType) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const payload = keyType === 'test' ? { test_api_key: '' } : { production_api_key: '' };
      const res = await axios.put(`${apiBase}/admin/users/${user.id}/urbanride-settings`, payload, { headers });
      setSettings(res.data);
      setSuccess(`${keyType === 'test' ? 'Test' : 'Production'} API key cleared`);
    } catch (e) {
      setError('Failed to clear API key');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Urbanride API Keys - {user.first_name} {user.last_name}
          </h3>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>

        {loading ? (
          <div className="p-6 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {/* Mode Override */}
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">API Mode Override</div>
              <p className="text-xs text-gray-500 mb-2">
                By default, users inherit the mode setting from their organization. Set an override to use a different mode for this specific user.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="urbanrideMode"
                    value="inherit"
                    checked={modeOverride === 'inherit'}
                    onChange={() => setModeOverride('inherit')}
                    className="w-4 h-4"
                  />
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                    Inherit from Organization
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="urbanrideMode"
                    value="test"
                    checked={modeOverride === 'test'}
                    onChange={() => setModeOverride('test')}
                    className="w-4 h-4"
                  />
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                    Test Mode
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="urbanrideMode"
                    value="production"
                    checked={modeOverride === 'production'}
                    onChange={() => setModeOverride('production')}
                    className="w-4 h-4"
                  />
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Production Mode
                  </span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {modeOverride === 'inherit'
                  ? 'User will use their organization\'s mode setting'
                  : modeOverride === 'test'
                    ? 'Override: Using test.urbanride.com - no real bookings'
                    : 'Override: Using apps.urbanride.com - LIVE bookings'
                }
              </p>
            </div>

            {/* API Key Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-1">Test API Key</div>
                {settings.test_api_key_configured ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-600">****{settings.test_api_key_hint}</span>
                    <button
                      onClick={() => handleClearKey('test')}
                      disabled={saving}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">Not configured</span>
                )}
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-1">Production API Key</div>
                {settings.production_api_key_configured ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-600">****{settings.production_api_key_hint}</span>
                    <button
                      onClick={() => handleClearKey('production')}
                      disabled={saving}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">Not configured</span>
                )}
              </div>
            </div>

            {/* Toggle to show/hide API key inputs */}
            <button
              onClick={() => setShowApiKeys(!showApiKeys)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {showApiKeys ? 'Hide API Key Fields' : 'Update API Keys'}
            </button>

            {/* API Key Inputs */}
            {showApiKeys && (
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Test API Key</label>
                  <input
                    type="password"
                    value={testApiKey}
                    onChange={(e) => setTestApiKey(e.target.value)}
                    placeholder={settings.test_api_key_configured ? 'Enter new key to replace' : 'Enter test API key'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Production API Key</label>
                  <input
                    type="password"
                    value={productionApiKey}
                    onChange={(e) => setProductionApiKey(e.target.value)}
                    placeholder={settings.production_api_key_configured ? 'Enter new key to replace' : 'Enter production API key'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {error && <div className="text-red-600 text-sm">{error}</div>}
            {success && <div className="text-green-600 text-sm">{success}</div>}
          </div>
        )}

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Users Tab Component
// Note: onUrbanrideSettings is only available for superadmins
const UsersTab = ({ users, organizations, isSuperadmin, currentUser, onInviteUser, onRemoveUser, onUpdateUserRole, onUpdateOrgAdmin, onUrbanrideSettings, executiveProfiles = [], onLinkExecutive }) => {
  const { labels, isSpeakerBureau } = useParadigm();
  const { actingOrganization } = useAuth();
  return (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Users</h2>
        <p className="text-gray-600 text-sm sm:text-base">
          {isSuperadmin ? 'Manage users across all organizations' : 'Manage users in your organization'}
        </p>
      </div>
      <button
        onClick={onInviteUser}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium w-full sm:w-auto"
      >
        + Invite User
      </button>
    </div>

    {/* Mobile Card View */}
    <div className="block md:hidden space-y-4">
      {users.map((userItem) => {
        const isSuperadminUser = userItem.role === 'superadmin';
        const isSpeakerPortalUser = userItem.role === 'speaker';
        const canModify = isSuperadmin || (!isSuperadminUser && userItem.id !== currentUser?.id);
        const primaryOrgName = organizations.find(org => org.id === userItem.organization_id)?.name || userItem.organization_id;
        // For multi-org members viewing via acting org, show the acting org name
        const actingOrgId = actingOrganization?.id;
        const isMultiOrgMember = actingOrgId && userItem.organization_id !== actingOrgId && (userItem.additional_organization_ids || []).includes(actingOrgId);
        const orgName = isMultiOrgMember ? (actingOrganization?.name || primaryOrgName) : primaryOrgName;

        return (
          <div key={userItem.id} className="bg-white rounded-lg shadow p-4 space-y-3">
            {/* User Info */}
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 truncate">
                    {userItem.first_name} {userItem.last_name}
                  </span>
                  {isSuperadminUser && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      Superadmin
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500 truncate">{userItem.email}</div>
                {isSuperadmin && (
                  <div className="text-xs text-gray-400 mt-1 truncate">{orgName}</div>
                )}
              </div>
              {/* Actions */}
              <div className="flex items-center gap-1 ml-2">
                {/* Urbanride settings - superadmin only; speakers never book rides */}
                {isSuperadmin && !isSpeakerPortalUser && (
                  <button
                    onClick={() => onUrbanrideSettings(userItem)}
                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                    title="Urbanride API Keys"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </button>
                )}
                {canModify && !isSuperadminUser && (
                  <button
                    onClick={() => onRemoveUser(userItem.id)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                    title="Remove User"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Role Controls */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-100">
              {isSpeakerPortalUser ? (
                <span
                  className="inline-flex items-center self-start px-3 py-2 rounded text-sm font-medium bg-rose-100 text-rose-800"
                  title="Speaker portal accounts are view-only; manage them from the Speakers page"
                >
                  Speaker (portal)
                </span>
              ) : (
              <select
                value={userItem.role}
                onChange={(e) => onUpdateUserRole(userItem.id, e.target.value)}
                disabled={!canModify || isSuperadminUser}
                className={`text-sm border border-gray-300 rounded px-3 py-2 flex-1 ${
                  (!canModify || isSuperadminUser) ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="ea">{labels.ea}</option>
                {(!isSpeakerBureau || userItem.role === 'executive') && (
                  <option value="executive">{labels.executive}</option>
                )}
                <option value="other">Other</option>
                {isSuperadminUser && <option value="superadmin">Superadmin</option>}
              </select>
              )}

              {!isSuperadminUser && !isSpeakerPortalUser && (
                <label className="flex items-center text-sm text-gray-600 px-2">
                  <input
                    type="checkbox"
                    checked={userItem.is_organization_admin || false}
                    onChange={(e) => onUpdateOrgAdmin(userItem.id, e.target.checked)}
                    disabled={!canModify}
                    className={`h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2 ${
                      !canModify ? 'cursor-not-allowed' : ''
                    }`}
                  />
                  Org Admin
                </label>
              )}

              {userItem.role === 'executive' && executiveProfiles.length > 0 && (
                <select
                  value={userItem.linked_executive_id || ''}
                  onChange={(e) => onLinkExecutive(userItem.id, e.target.value)}
                  disabled={!canModify}
                  className={`text-sm border border-gray-300 rounded px-3 py-2 flex-1 ${
                    !canModify ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="">Link to Executive Profile...</option>
                  {executiveProfiles.map((exec) => (
                    <option key={exec.id} value={exec.id}>
                      {exec.first_name} {exec.last_name}{exec.email ? ` (${exec.email})` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        );
      })}
    </div>

    {/* Desktop Table View */}
    <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
            {isSuperadmin && (
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
            )}
            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((userItem) => {
            const isSuperadminUser = userItem.role === 'superadmin';
            const isSpeakerPortalUser = userItem.role === 'speaker';
            const canModify = isSuperadmin || (!isSuperadminUser && userItem.id !== currentUser?.id);

            return (
              <tr key={userItem.id} className="hover:bg-gray-50">
                <td className="px-4 lg:px-6 py-4">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                      <span className="truncate">{userItem.first_name} {userItem.last_name}</span>
                      {isSuperadminUser && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 flex-shrink-0">
                          Superadmin
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 truncate">{userItem.email}</div>
                  </div>
                </td>
                {isSuperadmin && (
                  <td className="px-4 lg:px-6 py-4 text-sm text-gray-900">
                    <span className="truncate block max-w-[200px]">
                      {(() => {
                        const primaryName = organizations.find(org => org.id === userItem.organization_id)?.name || userItem.organization_id;
                        const actOrgId = actingOrganization?.id;
                        const isMultiOrg = actOrgId && userItem.organization_id !== actOrgId && (userItem.additional_organization_ids || []).includes(actOrgId);
                        return isMultiOrg ? (actingOrganization?.name || primaryName) : primaryName;
                      })()}
                    </span>
                  </td>
                )}
                <td className="px-4 lg:px-6 py-4">
                  <div className="space-y-2">
                    {isSpeakerPortalUser ? (
                      <span
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-rose-100 text-rose-800"
                        title="Speaker portal accounts are view-only; manage them from the Speakers page"
                      >
                        Speaker (portal)
                      </span>
                    ) : (
                    <select
                      value={userItem.role}
                      onChange={(e) => onUpdateUserRole(userItem.id, e.target.value)}
                      disabled={!canModify || isSuperadminUser}
                      className={`text-sm border border-gray-300 rounded px-2 py-1 w-full max-w-[140px] ${
                        (!canModify || isSuperadminUser) ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    >
                      <option value="ea">{labels.ea}</option>
                      {(!isSpeakerBureau || userItem.role === 'executive') && (
                        <option value="executive">{labels.executive}</option>
                      )}
                      <option value="other">Other</option>
                      {isSuperadminUser && <option value="superadmin">Superadmin</option>}
                    </select>
                    )}

                    {!isSuperadminUser && !isSpeakerPortalUser && (
                      <label className="flex items-center text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={userItem.is_organization_admin || false}
                          onChange={(e) => onUpdateOrgAdmin(userItem.id, e.target.checked)}
                          disabled={!canModify}
                          className={`h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-1 ${
                            !canModify ? 'cursor-not-allowed' : ''
                          }`}
                        />
                        Org Admin
                      </label>
                    )}

                    {userItem.role === 'executive' && executiveProfiles.length > 0 && (
                      <select
                        value={userItem.linked_executive_id || ''}
                        onChange={(e) => onLinkExecutive(userItem.id, e.target.value)}
                        disabled={!canModify}
                        className={`text-xs border border-gray-300 rounded px-2 py-1 w-full max-w-[180px] ${
                          !canModify ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      >
                        <option value="">Link to Executive...</option>
                        {executiveProfiles.map((exec) => (
                          <option key={exec.id} value={exec.id}>
                            {exec.first_name} {exec.last_name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 text-sm font-medium">
                  <div className="flex justify-center gap-1">
                    {/* Urbanride settings - superadmin only; speakers never book rides */}
                    {isSuperadmin && !isSpeakerPortalUser && (
                      <button
                        onClick={() => onUrbanrideSettings(userItem)}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                        title="Urbanride API Keys"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                      </button>
                    )}
                    {canModify && !isSuperadminUser ? (
                      <button
                        onClick={() => onRemoveUser(userItem.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                        title="Remove User"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    ) : (
                      <span className="p-2 text-gray-300" title="Cannot modify">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);
};

// Invites Tab Component
const InvitesTab = ({ invites, onApproveInvite, onRejectInvite }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-xl font-bold text-gray-900">Pending Invites</h2>
      <p className="text-gray-600 text-sm sm:text-base">Review and approve user invitations</p>
    </div>

    {invites.length === 0 ? (
      <div className="bg-white rounded-lg shadow text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">📧</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No pending invites</h3>
        <p className="text-gray-500">All invitations have been processed</p>
      </div>
    ) : (
      <>
        {/* Mobile Card View */}
        <div className="block md:hidden space-y-4">
          {invites.map((invite) => (
            <div key={invite.id} className="bg-white rounded-lg shadow p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">{invite.email}</div>
                  <div className="text-sm text-gray-500 truncate">{invite.organization_name}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {invite.role}
                    </span>
                    <span className="text-xs text-gray-400">by {invite.invited_by_name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <button
                    onClick={() => onApproveInvite(invite.id)}
                    className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
                    title="Approve Invite"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onRejectInvite(invite.id)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                    title="Reject Invite"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invited By</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invites.map((invite) => (
                <tr key={invite.id} className="hover:bg-gray-50">
                  <td className="px-4 lg:px-6 py-4 text-sm font-medium text-gray-900">
                    <span className="truncate block max-w-[200px]">{invite.email}</span>
                  </td>
                  <td className="px-4 lg:px-6 py-4 text-sm text-gray-900">
                    <span className="truncate block max-w-[150px]">{invite.organization_name}</span>
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {invite.role}
                    </span>
                  </td>
                  <td className="px-4 lg:px-6 py-4 text-sm text-gray-500">
                    <span className="truncate block max-w-[120px]">{invite.invited_by_name}</span>
                  </td>
                  <td className="px-4 py-4 text-sm font-medium">
                    <div className="flex flex-row space-x-2 justify-center">
                      <button
                        onClick={() => onApproveInvite(invite.id)}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
                        title="Approve Invite"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onRejectInvite(invite.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                        title="Reject Invite"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )}
  </div>
);

// Tutorial Settings Card - allows org admins to toggle tutorials for their organization
const TutorialSettingsCard = () => {
  const { user } = useAuth();
  const [tutorialEnabled, setTutorialEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const orgId = user?.organization_id;

  useEffect(() => {
    if (!orgId) return;
    const load = async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const res = await axios.get(`${apiBase}/organizations/${orgId}/tutorial-settings`, { headers });
        setTutorialEnabled(res.data.tutorial_enabled);
      } catch (e) {
        console.log('Could not load tutorial settings:', e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId]);

  const handleToggle = async () => {
    setSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const newValue = !tutorialEnabled;
      await axios.put(`${apiBase}/organizations/${orgId}/tutorial-settings`, { tutorial_enabled: newValue }, { headers });
      setTutorialEnabled(newValue);
    } catch (e) {
      console.error('Failed to update tutorial settings:', e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <span className="text-xl">🎓</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Quick Start Tutorial</h3>
            <p className="text-sm text-gray-600">Control the guided tutorial for your organization's users</p>
          </div>
        </div>
      </div>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900">Enable Tutorial</p>
            <p className="text-sm text-gray-500">
              {tutorialEnabled
                ? 'New users will see the guided tutorial on first login'
                : 'Tutorial is disabled for all users in this organization'}
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              tutorialEnabled ? 'bg-blue-600' : 'bg-gray-200'
            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            role="switch"
            aria-checked={tutorialEnabled}
            aria-label="Toggle tutorial"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                tutorialEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

// Settings Tab Component (Superadmin only - Urbanride Account Linking)
const SettingsTab = ({ isSuperadmin }) => {
  const [organizations, setOrganizations] = useState([]);
  const [urbanrideAccounts, setUrbanrideAccounts] = useState({ test: [], production: [] });
  const [loading, setLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState({ test: false, production: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingOrg, setEditingOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [appSettings, setAppSettings] = useState({ urbanride_default_mode: 'test' });
  const [savingAppSettings, setSavingAppSettings] = useState(false);

  // Load organizations with Urbanride settings and app settings
  useEffect(() => {
    if (isSuperadmin) {
      loadOrganizations();
      loadAppSettings();
      loadUrbanrideAccounts('test');
      loadUrbanrideAccounts('production');
    }
  }, [isSuperadmin]);

  const loadAppSettings = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/admin/app-settings`, { headers });
      setAppSettings(res.data);
    } catch (e) {
      console.log('Could not load app settings:', e?.response?.data?.detail || e.message);
    }
  };

  const handleAppSettingsChange = async (newDefaultMode) => {
    setSavingAppSettings(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/app-settings`, { urbanride_default_mode: newDefaultMode }, { headers });
      setAppSettings({ ...appSettings, urbanride_default_mode: newDefaultMode });
      setSuccess(`Default mode for new organizations set to ${newDefaultMode}`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save app settings');
    } finally {
      setSavingAppSettings(false);
    }
  };

  const handleOrgModeChange = async (orgId, newMode) => {
    setSaving(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/organizations/${orgId}/urbanride-settings`, { mode: newMode }, { headers });

      // Update local state
      setOrganizations(orgs => orgs.map(org =>
        org.organization_id === orgId ? { ...org, mode: newMode } : org
      ));

      setSuccess(`Organization mode updated to ${newMode}`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to update organization mode');
    } finally {
      setSaving(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/admin/urbanride/organizations`, { headers });
      setOrganizations(res.data.organizations || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const loadUrbanrideAccounts = async (mode) => {
    try {
      setAccountsLoading(prev => ({ ...prev, [mode]: true }));
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/admin/urbanride/accounts?mode=${mode}`, { headers });
      setUrbanrideAccounts(prev => ({ ...prev, [mode]: res.data.accounts || [] }));
    } catch (e) {
      // Don't show error - Bot API may not be configured
      console.log(`Could not load ${mode} Urbanride accounts:`, e?.response?.data?.detail || e.message);
    } finally {
      setAccountsLoading(prev => ({ ...prev, [mode]: false }));
    }
  };

  const handleEditOrg = (org) => {
    setEditingOrg({
      ...org,
      account_id_test: org.account_id_test || '',
      account_id_production: org.account_id_production || '',
      auto_provision_enabled: org.auto_provision_enabled !== false
    });
  };

  const handleSave = async () => {
    if (!editingOrg) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Find account names for selected IDs
      const testAccount = urbanrideAccounts.test.find(a => a.id === parseInt(editingOrg.account_id_test));
      const prodAccount = urbanrideAccounts.production.find(a => a.id === parseInt(editingOrg.account_id_production));

      const payload = {
        account_id_test: editingOrg.account_id_test ? parseInt(editingOrg.account_id_test) : null,
        account_name_test: testAccount?.name || null,
        account_id_production: editingOrg.account_id_production ? parseInt(editingOrg.account_id_production) : null,
        account_name_production: prodAccount?.name || null,
        auto_provision_enabled: editingOrg.auto_provision_enabled
      };

      await axios.put(`${apiBase}/admin/organizations/${editingOrg.organization_id}/urbanride-settings`, payload, { headers });

      // Update local state
      setOrganizations(orgs => orgs.map(org =>
        org.organization_id === editingOrg.organization_id
          ? {
              ...org,
              account_id_test: payload.account_id_test,
              account_name_test: payload.account_name_test,
              account_id_production: payload.account_id_production,
              account_name_production: payload.account_name_production,
              auto_provision_enabled: payload.auto_provision_enabled
            }
          : org
      ));

      setSuccess(`Urbanride settings updated for ${editingOrg.organization_name}`);
      setEditingOrg(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAccount = async (orgId, mode) => {
    setSaving(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const payload = mode === 'test'
        ? { account_id_test: null, account_name_test: null }
        : { account_id_production: null, account_name_production: null };

      await axios.put(`${apiBase}/admin/organizations/${orgId}/urbanride-settings`, payload, { headers });

      // Update local state
      setOrganizations(orgs => orgs.map(org =>
        org.organization_id === orgId
          ? { ...org, ...payload }
          : org
      ));

      setSuccess(`${mode === 'test' ? 'Test' : 'Production'} account unlinked`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to clear account');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="space-y-6">
        <TutorialSettingsCard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TutorialSettingsCard />

      {/* Header */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <span className="text-xl">🚗</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Urbanride Account Linking</h3>
              <p className="text-sm text-gray-600">Link organizations with Urbanride accounts for automatic API key provisioning</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Status Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md flex justify-between items-center">
              <span>{error}</span>
              <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">×</button>
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md flex justify-between items-center">
              <span>{success}</span>
              <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">×</button>
            </div>
          )}

          {/* App-Level Default Mode Setting */}
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-purple-900">Default Mode for New Organizations</h4>
                <p className="text-sm text-purple-700">This setting applies when new organizations are created</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={appSettings.urbanride_default_mode || 'test'}
                  onChange={(e) => handleAppSettingsChange(e.target.value)}
                  disabled={savingAppSettings}
                  className="px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="test">Test Mode</option>
                  <option value="production">Production Mode</option>
                </select>
                {savingAppSettings && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                )}
              </div>
            </div>
          </div>

          {/* Information Box */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">How it works</h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Link each organization to an Urbanride account</li>
              <li>When users first try to book, their API key will be automatically created</li>
              <li>Users are matched by email address to existing Urbanride bookers</li>
              <li>New bookers are created automatically for users without existing profiles</li>
            </ul>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      <span className="inline-flex items-center gap-1">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                        Test Account
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      <span className="inline-flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Production Account
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Auto-Provision</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Mode</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {organizations.map((org) => (
                    <tr key={org.organization_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{org.organization_name}</td>
                      <td className="px-4 py-3 text-sm">
                        {org.account_id_test ? (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-900">{org.account_name_test || `ID: ${org.account_id_test}`}</span>
                            <button
                              onClick={() => handleClearAccount(org.organization_id, 'test')}
                              className="text-xs text-red-600 hover:text-red-800"
                              disabled={saving}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">Not linked</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {org.account_id_production ? (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-900">{org.account_name_production || `ID: ${org.account_id_production}`}</span>
                            <button
                              onClick={() => handleClearAccount(org.organization_id, 'production')}
                              className="text-xs text-red-600 hover:text-red-800"
                              disabled={saving}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">Not linked</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          org.auto_provision_enabled !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {org.auto_provision_enabled !== false ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <select
                          value={org.mode || 'test'}
                          onChange={(e) => handleOrgModeChange(org.organization_id, e.target.value)}
                          disabled={saving}
                          className={`text-sm border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                            (org.mode || 'test') === 'production'
                              ? 'border-green-300 bg-green-50 text-green-800'
                              : 'border-yellow-300 bg-yellow-50 text-yellow-800'
                          }`}
                        >
                          <option value="test">Test</option>
                          <option value="production">Production</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEditOrg(org)}
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Link Urbanride Accounts - {editingOrg.organization_name}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Test Account Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                    Test Environment Account
                  </span>
                </label>
                <select
                  value={editingOrg.account_id_test || ''}
                  onChange={(e) => setEditingOrg({ ...editingOrg, account_id_test: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={accountsLoading.test}
                >
                  <option value="">-- Not linked --</option>
                  {urbanrideAccounts.test.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} (ID: {account.id})
                    </option>
                  ))}
                </select>
                {urbanrideAccounts.test.length === 0 && !accountsLoading.test && (
                  <p className="mt-1 text-xs text-amber-600">
                    No accounts available. Check Bot API configuration.
                  </p>
                )}
              </div>

              {/* Production Account Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Production Environment Account
                  </span>
                </label>
                <select
                  value={editingOrg.account_id_production || ''}
                  onChange={(e) => setEditingOrg({ ...editingOrg, account_id_production: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={accountsLoading.production}
                >
                  <option value="">-- Not linked --</option>
                  {urbanrideAccounts.production.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} (ID: {account.id})
                    </option>
                  ))}
                </select>
                {urbanrideAccounts.production.length === 0 && !accountsLoading.production && (
                  <p className="mt-1 text-xs text-amber-600">
                    No accounts available. Check Bot API configuration.
                  </p>
                )}
              </div>

              {/* Auto-Provision Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">Auto-Provision API Keys</h4>
                  <p className="text-xs text-gray-600">Automatically create keys for users</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingOrg.auto_provision_enabled}
                    onChange={(e) => setEditingOrg({ ...editingOrg, auto_provision_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setEditingOrg(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
